import { useState, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import './App.css';
import saucebookIcon from './saucebookicon.jpg';

const GET_OPEN_EDITIONS = gql`
  query GetOpenEditions($orderBy: [open_edition_active_order_by!]) {
    open_edition_active(order_by: $orderBy) {
      token {
        name
        mime
        display_uri
        thumbnail_uri
        artifact_uri
        pk
        token_id
        creators {
          holder {
            alias
          }
        }
        fa {
          contract
        }
        supply
      }
      price
      end_time
    }
  }
`;

// Serve media through objkt's own asset CDN instead of the public ipfs.io
// gateway, which rate-limits a page firing hundreds of image requests at once.
const cdnUrl = (ipfsUri) =>
  `https://assets.objkt.media/file/assets-003/${ipfsUri.replace('ipfs://', '')}/artifact`;
const gatewayUrl = (ipfsUri) =>
  `https://dweb.link/ipfs/${ipfsUri.replace('ipfs://', '')}`;

const PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400"><rect width="100%" height="100%" fill="%23f3e9d2"/><text x="50%" y="50%" text-anchor="middle" fill="%23999" font-family="sans-serif" font-size="20">no preview</text></svg>'
  );

const TokenMedia = ({ token }) => {
  // Prefer the small thumbnail for the grid; fall back to display, then artifact.
  const uri = token.thumbnail_uri || token.display_uri || token.artifact_uri;
  const fallbackUri = token.display_uri || token.artifact_uri;
  const [stage, setStage] = useState(0);

  if (!uri) {
    return <img src={PLACEHOLDER} className="card-img-top" alt={token.name} referrerPolicy="no-referrer" />;
  }

  const sources = [cdnUrl(uri)];
  if (fallbackUri && fallbackUri !== uri) sources.push(cdnUrl(fallbackUri));
  sources.push(gatewayUrl(fallbackUri || uri));
  sources.push(PLACEHOLDER);

  return (
    <img
      src={sources[Math.min(stage, sources.length - 1)]}
      className="card-img-top"
      alt={token.name}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setStage((s) => s + 1)}
    />
  );
};

const TimeRemaining = ({ endTime }) => {
  const calculateTimeLeft = () => {
    const difference = +new Date(endTime) - +new Date();
    let timeLeft = {};

    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
      };
    }

    return timeLeft;
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearTimeout(timer);
  });

  const timerComponents = [];

  Object.keys(timeLeft).forEach((interval) => {
    if (!timeLeft[interval]) {
      return;
    }

    timerComponents.push(
      <span key={interval}>
        {timeLeft[interval]} {interval}{" "}
      </span>
    );
  });

  return <div>{timerComponents.length ? timerComponents : <span>Finished!</span>}</div>;
};

function App() {
  const [sortBy, setSortBy] = useState({ price: "asc" });
  const [sortValue, setSortValue] = useState('price_asc');

  const { loading, error, data } = useQuery(GET_OPEN_EDITIONS, {
    variables: { orderBy: sortBy },
    pollInterval: 60000, // Poll every minute to update timers
  });

  const handleSortChange = (e) => {
    const value = e.target.value;
    setSortValue(value);
    let newSortBy;
    switch (value) {
      case 'price_asc':
        newSortBy = { price: "asc" };
        break;
      case 'price_desc':
        newSortBy = { price: "desc" };
        break;
      case 'finishing_soonest':
        newSortBy = { end_time: "asc" };
        break;
      case 'finishing_latest':
        newSortBy = { end_time: "desc" };
        break;
      case 'supply_asc':
        newSortBy = { token: { supply: "asc" } };
        break;
      case 'supply_desc':
        newSortBy = { token: { supply: "desc" } };
        break;
      default:
        newSortBy = { price: "asc" };
    }
    setSortBy(newSortBy);
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error :(</p>;

  return (
    <div className="container">
      <img src={saucebookIcon} className="saucebook-icon" alt="Saucebook" />
      <div className="header">
        <h1>OpenSauce</h1>
        <p style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>Catch them while you can</p>
        <p>(powered by Objkt.com api)</p>
      </div>
      <div className="form-group sort-by-container">
        <label htmlFor="sort-by">Sort By:</label>
        <select id="sort-by" className="form-control" onChange={handleSortChange} value={sortValue}>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
          <option value="finishing_soonest">Finishing Soonest</option>
          <option value="finishing_latest">Finishing Latest</option>
          <option value="supply_asc">Supply: Low to High</option>
          <option value="supply_desc">Supply: High to Low</option>
        </select>
      </div>
      <div className="row">
        {data && data.open_edition_active && data.open_edition_active.map(({ token, price, end_time }) => (
          <div className="col-md-4" key={token.pk}>
            <div className="card mb-4 shadow-sm">
              <TokenMedia token={token} />
              <div className="card-body">
                <h5 className="card-title">{token.name}</h5>
                <p className="card-text">Artist: {token.creators[0]?.holder?.alias || 'Unknown'}</p>
                <p className="card-text">Price: {price / 1000000} tez</p>
                <p className="card-text">Minted: {token.supply}</p>
                {end_time && <TimeRemaining endTime={end_time} />}
                <a
                  href={`https://objkt.com/tokens/${token.fa.contract}/${token.token_id}?ref=tz1fEGoyctWKrPrjaSvoTe7YvG8pRU581k9D`}
                  className="btn btn-primary mt-2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Buy Now
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;
